import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { useFloatingTrainingVideo } from '../context/FloatingTrainingVideoContext';

const MINI_WIDTH = 300;
const Z_OVERLAY = 1400;

function useDockRect(mountEl: HTMLElement | null, enabled: boolean) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!enabled || !mountEl) {
      setRect(null);
      return undefined;
    }

    const update = () => setRect(mountEl.getBoundingClientRect());
    update();

    const observer = new ResizeObserver(update);
    observer.observe(mountEl);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [mountEl, enabled]);

  return rect;
}

export const FloatingTrainingVideoPlayer: React.FC = () => {
  const theme = useTheme();
  const { active, mode, dockMountEl, minimize, expand, close } = useFloatingTrainingVideo();
  const dockRect = useDockRect(dockMountEl, mode === 'docked');

  const shellSx = useMemo(() => {
    if (!active || mode === 'idle') return null;

    if (mode === 'docked' && dockRect) {
      return {
        position: 'fixed' as const,
        top: dockRect.top,
        left: dockRect.left,
        width: dockRect.width,
        height: dockRect.height,
        zIndex: 5,
        borderRadius: 1.5,
        overflow: 'hidden',
        boxShadow: 'none',
        bgcolor: 'transparent',
      };
    }

    if (mode === 'mini') {
      return {
        position: 'fixed' as const,
        right: 16,
        bottom: 16,
        width: MINI_WIDTH,
        zIndex: Z_OVERLAY,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
      };
    }

    if (mode === 'expanded') {
      return {
        position: 'fixed' as const,
        top: '10vh',
        left: '10vw',
        width: '80vw',
        height: '80vh',
        zIndex: Z_OVERLAY,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
      };
    }

    return null;
  }, [active, mode, dockRect, theme.palette.mode]);

  if (!active || mode === 'idle' || !shellSx) return null;

  return (
    <>
      {mode === 'expanded' && (
        <Box
          onClick={minimize}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: Z_OVERLAY - 1,
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        />
      )}

      <Paper elevation={mode === 'docked' ? 0 : mode === 'mini' ? 8 : 12} sx={shellSx}>
        {mode === 'mini' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {active.title}
            </Typography>
            <Stack direction="row" spacing={0.25}>
              <IconButton size="small" onClick={expand} aria-label="Ampliar vídeo">
                <OpenInFullIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={close} aria-label="Fechar vídeo">
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
        )}

        {mode === 'expanded' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              py: 1.25,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {active.categoryTitle}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {active.title}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              <IconButton size="small" onClick={minimize} aria-label="Miniaturizar vídeo">
                <PictureInPictureAltIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={close} aria-label="Fechar vídeo">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            position: 'relative',
            flex: mode === 'expanded' ? 1 : undefined,
            minHeight: mode === 'expanded' ? 0 : undefined,
            pt: mode === 'mini' ? '56.25%' : undefined,
            width: '100%',
            height: mode === 'docked' ? '100%' : undefined,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(15,23,42,0.06)',
          }}
        >
          <Box
            component="iframe"
            title={active.title}
            src={active.embedSrc}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>
      </Paper>
    </>
  );
};
